import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Button } from '@/components/button'
import ErrorModal from '@/components/error-modal'
import { FormInput } from '@/components/form-fields'
import { CustomMultiSelect } from '@/components/form/custom-multiselect'
import {
  CreateSKUSchema,
  UpdateSkuCodeType,
  UpdateSKUSchema,
} from '@/schema/sku-builder.schema'
import { CACHE_KEYS, invalidateIncentiveQueries } from '@/lib/cache-utils'
import { useSkuCodeClient, useIncentiveClient } from '@/lib/api'

// Define type locally (previously from GraphQL)
type SkuCodeIncentiveItem = {
  id: string
  name: string
  skuCode?: string
  incentiveItem?: {
    id: string
    name: string
  }
}

export default function SkuFormBuilder() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const skuCodeClient = useSkuCodeClient()
  const incentiveClient = useIncentiveClient()

  // Fetch all incentives using ts-rest
  const {
    data: incentivesResponse,
    isLoading: loadingIncentives,
    error,
  } = useQuery({
    queryKey: ['incentives'],
    queryFn: async () => {
      const response = await incentiveClient.getAll.query()
      return response.status === 200 ? response.body : []
    },
  })

  const incentivesData = useMemo(
    () =>
      (incentivesResponse || []).filter((incentive: any) => incentive.isActive),
    [incentivesResponse],
  )
  const incentivesOptions =
    incentivesData
      ?.map((incentive) => ({
        value: incentive.id,
        label: incentive.customer?.name
          ? `${incentive.name} (${incentive.customer.name})`
          : incentive.name,
        picture: incentive.picture,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []

  const { data: skuResponse } = useQuery({
    queryKey: ['skuCode', id],
    queryFn: async () =>
      await skuCodeClient.getById.query({ params: { id: id! } }),
    enabled: !!id,
  })

  const skuData = useMemo(
    () => ({
      skuCode: (skuResponse?.body ?? null) as any,
    }),
    [skuResponse?.body],
  )

  // Initialize form with react-hook-form
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<UpdateSkuCodeType>({
    resolver: zodResolver(id ? UpdateSKUSchema : CreateSKUSchema),
    defaultValues: {
      name: '',
      incentiveItemIds: [],
    },
  })

  const [errorMessage, setErrorMessage] = useState<{
    title: string
    text: string
  } | null>(null)
  const [showErrorModal, setShowErrorModal] = useState(false)

  const handleMutationError = (error: Error, skuName: string) => {
    setErrorMessage({
      title: t('error'),
      text: error.message?.includes('already exists')
        ? t('sku_builder.sku_exists', { name: skuName })
        : error.message || t('error'),
    })
    setShowErrorModal(true)
  }

  const createSkuCodeMutation = useMutation({
    mutationFn: async (skuCodeInput: UpdateSkuCodeType) => {
      const response = await skuCodeClient.create.mutate({
        body: skuCodeInput,
      })
      // The ts-rest client resolves on non-2xx, so throw to reach onError
      if (response.status !== 201) {
        const body = response.body as { message?: string }
        throw new Error(body?.message || 'Failed to create SKU code')
      }
      return response
    },
    onSuccess: async () => {
      // refetchType: 'all' — the global client sets refetchOnMount: false,
      // so the unmounted SKU list would otherwise render stale after navigate
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.SKU_CODES],
          refetchType: 'all',
        }),
        // SKU changes also rewrite incentiveItem.skuCode on the server
        invalidateIncentiveQueries(queryClient),
      ])
      navigate('/incentives/sku-list')
    },
    onError: (error, skuCodeInput) => {
      handleMutationError(error, skuCodeInput.name)
    },
  })

  const updateSkuCodeMutation = useMutation({
    mutationFn: async (skuCodeInput: UpdateSkuCodeType) => {
      const response = await skuCodeClient.update.mutate({
        params: { id: skuCodeInput.id },
        body: skuCodeInput,
      })
      if (response.status !== 200) {
        const body = response.body as { message?: string }
        throw new Error(body?.message || 'Failed to update SKU code')
      }
      return response
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.SKU_CODES],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.SKU_CODE, id],
          refetchType: 'all',
        }),
        invalidateIncentiveQueries(queryClient),
      ])
      navigate('/incentives/sku-list')
    },
    onError: (error, skuCodeInput) => {
      handleMutationError(error, skuCodeInput.name)
    },
  })

  useEffect(() => {
    if (skuData?.skuCode) {
      const formValues: UpdateSkuCodeType = {
        id: skuData.skuCode.id,
        name: skuData.skuCode.name,
        incentiveItemIds: skuData.skuCode.incentiveItems?.map(
          (item: SkuCodeIncentiveItem) => item.incentiveItem?.id ?? '',
        ),
      }
      setValue('id', formValues.id)
      setValue('name', formValues.name)
      setValue('incentiveItemIds', formValues.incentiveItemIds || [])
    }
  }, [skuData, setValue])

  const onSubmit = (formData: UpdateSkuCodeType) => {
    if (id) {
      return updateSkuCodeMutation.mutate({
        ...formData,
        id: id,
      })
    }
    return createSkuCodeMutation.mutate(formData)
  }

  if (loadingIncentives) return <div>{t('loading')}</div>
  if (error) return <div>{t('error')}</div>

  return (
    <div className="p-8 space-y-2 flex flex-col max-w-2xl m-auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center mt-10 mb-14">
          <Link
            to="/incentives/sku-list"
            className="flex items-center hover:text-primary"
          >
            <ArrowLeft className="w-8 h-8" />
          </Link>
          <h1 className="font-bold text-3xl mx-6 mb-0">
            {id
              ? t('sku_builder.update_sku_code')
              : t('sku_builder.add_sku_code')}
          </h1>
        </div>
        <FormInput
          name="name"
          control={control}
          label={t('incentives.sku_code')}
          type="text"
          disabled={!!id}
          inputClassName={`border rounded px-3 py-2 w-full ${
            id ? 'bg-gray-100' : ''
          }`}
          testId="sku"
        />

        <div className="flex flex-col gap-1 my-4">
          <label>{t('sku_builder.assign_incentive')}</label>
          <CustomMultiSelect
            name="incentiveItemIds"
            control={control}
            options={incentivesOptions}
            placeholder={t('sku_builder.select_incentives')}
            className="w-full"
            searchable
            searchPlaceholder={t('sku_builder.search_incentives')}
            emptyText={t('sku_builder.no_incentives_found')}
            showSelectedChips
          />
          {errors.incentiveItemIds && (
            <span className="text-primary">
              {t('validation_messages.required')}
            </span>
          )}
        </div>

        <div className="flex justify-end my-10">
          <div className="grid grid-cols-2 gap-4 my-20">
            <Button
              type="button"
              name="cancel"
              label={t('cancel')}
              size="small"
              onClick={() => {
                navigate('/incentives')
              }}
              data-testid="incentive-cancel"
            />
            <Button
              primary={true}
              type="submit"
              name="add-incentive"
              label={
                !id
                  ? t('sku_builder.add_sku_code')
                  : t('sku_builder.update_sku_code')
              }
              size="small"
              data-testid="incentive-submit"
            />
          </div>
        </div>
      </form>
      {showErrorModal &&
        createPortal(
          <ErrorModal
            title={errorMessage?.title ?? ''}
            error={errorMessage?.text ?? ''}
            onClick={() => setShowErrorModal(false)}
            submitButtonText=""
          />,
          document.body,
        )}
    </div>
  )
}
